import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {useTenant} from '../App';
import {publicApi} from '../api';
import Spinner from '../components/Spinner';
import Footer from '../components/Footer';
import usePageTitle from '../hooks/usePageTitle';

function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
}

export default function JobBoard() {
  const tenant = useTenant();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dept, setDept] = useState('');
  const [loc, setLoc] = useState('');
  const [type, setType] = useState('');

  usePageTitle('Open Positions', `Browse open positions at ${tenant.name} and apply online.`);

  useEffect(() => {
    publicApi(`/ats/public/jobs?tenantId=${tenant.id}`).then(r => r.ok ? r.json() : []).then(setJobs).finally(() => setLoading(false));
  }, [tenant.id]);

  const departments = [...new Set(jobs.map(j => j.department).filter(Boolean))].sort();
  const locations = [...new Set(jobs.map(j => j.location).filter(Boolean))].sort();
  const types = [...new Set(jobs.map(j => j.employmentType).filter(Boolean))].sort();
  const hasFilters = search || dept || loc || type;

  const filtered = jobs.filter(j => {
    if (dept && j.department !== dept) return false;
    if (loc && j.location !== loc) return false;
    if (type && j.employmentType !== type) return false;
    if (search && !j.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{tenant.name}</h1>
            <p className="text-gray-500 mt-1">{jobs.length} open position{jobs.length !== 1 ? 's' : ''}</p>
          </div>
          <Link to="/check-status" className="text-sm text-teal-600 hover:text-teal-800 font-medium focus:outline-none focus:underline">
            Check Application Status →
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 flex-1 w-full">
        <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-6" role="search" aria-label="Filter positions">
          <input
            placeholder="Search positions..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            aria-label="Search positions"
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 w-full sm:w-64"
          />
          {departments.length > 1 && (
            <select value={dept} onChange={e => setDept(e.target.value)} aria-label="Filter by department" className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
              <option value="">All Departments</option>
              {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          )}
          {locations.length > 1 && (
            <select value={loc} onChange={e => setLoc(e.target.value)} aria-label="Filter by location" className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
              <option value="">All Locations</option>
              {locations.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          )}
          {types.length > 1 && (
            <select value={type} onChange={e => setType(e.target.value)} aria-label="Filter by type" className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
              <option value="">All Types</option>
              {types.map(t => <option key={t} value={t}>{(t || '').replace('_', ' ')}</option>)}
            </select>
          )}
          {hasFilters && (
            <button onClick={() => { setSearch(''); setDept(''); setLoc(''); setType(''); }} className="text-sm text-gray-500 hover:text-gray-700 focus:outline-none focus:underline">
              Clear filters
            </button>
          )}
        </div>

        {loading ? (
          <Spinner className="py-20" />
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-lg shadow fade-in">
            <div className="text-4xl mb-4">{jobs.length === 0 ? '📋' : '🔍'}</div>
            <p className="text-gray-500 text-lg">{jobs.length === 0 ? 'No open positions at this time.' : 'No positions match your filters.'}</p>
            <p className="text-gray-400 mt-2 text-sm">{jobs.length === 0 ? 'Check back later for new opportunities.' : 'Try adjusting your search or filters.'}</p>
          </div>
        ) : (
          <div className="space-y-4" role="list" aria-label="Job listings">
            {filtered.map(job => {
              const preview = stripHtml(job.description);
              return (
                <Link key={job.id} to={`/jobs/${job.id}`} className="block bg-white rounded-lg shadow hover:shadow-md transition-all duration-200 p-5 sm:p-6 fade-in focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 rounded-lg" role="listitem">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h2 className="text-lg font-semibold text-gray-900">{job.title}</h2>
                      <div className="flex flex-wrap gap-2 mt-2 text-sm text-gray-500">
                        {job.department && <span className="bg-gray-100 px-2 py-0.5 rounded">{job.department}</span>}
                        {job.location && <span className="bg-gray-100 px-2 py-0.5 rounded">📍 {job.location}</span>}
                        {job.employmentType && <span className="bg-gray-100 px-2 py-0.5 rounded">{job.employmentType.replace('_', ' ')}</span>}
                        {(job.salaryMin || job.salaryMax) && (
                          <span className="bg-gray-100 px-2 py-0.5 rounded">
                            {job.salaryMin ? `$${Number(job.salaryMin).toLocaleString()}` : ''}
                            {job.salaryMin && job.salaryMax ? ' – ' : ''}
                            {job.salaryMax ? `$${Number(job.salaryMax).toLocaleString()}` : ''}
                          </span>
                        )}
                      </div>
                      {preview && <p className="text-sm text-gray-500 mt-3 line-clamp-2">{preview}</p>}
                    </div>
                    <span className="text-teal-600 text-sm font-medium flex-shrink-0 hidden sm:block" aria-hidden="true">View →</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
